import React from "react";
import {
  Alert,
  Checkbox,
  CheckboxGroup,
  Column,
  Columns,
  Heading,
  Stack,
} from "@origin-digital/ods-core";
import { ServiceTypeEnum } from "src/components/Premises";
import { CTReadOnlyServiceSection } from "../CTReadOnlyServiceSection";
import FormFieldLayout from "../../FormComponents/FormFieldLayout";
import DatePickerField from "../../FormComponents/DatePickerField";
import {
  formatDateToString,
  handoverTypeToLabelMap,
  handoverTypeToLabelMapPassive,
} from "../helper";
import { useCloseTransferFormProps } from "./useCloseTransferFormProps";
import { AccountPayer, HandoverTypeEnum } from "../../types";

type Props = {
  onUpdate: (
    serviceType: ServiceTypeEnum,
    actionType: "cancel" | "action"
  ) => Promise<void>;
  elecDates: { dates: string[] } | undefined;
  gasDates: { dates: string[] } | undefined;
};

type INFO = {
  section: ServiceTypeEnum;
};

export const CloseTransferFormAutomatedServiceSection: React.FC<Props> = ({
  onUpdate,
  elecDates,
  gasDates,
}) => {
  const { formikContext, formBehavior, selectedFormState } =
    useCloseTransferFormProps();
  const { values, setFieldValue, errors } = formikContext;
  const { closeTransfer } = values;
  const {
    hasElectricitySection,
    isElectricityEnable,
    isElectricityReadOnly,
    hasGasSection,
    isGasEnable,
    isGasReadOnly,
  } = formBehavior;
  const { gasServices } = selectedFormState;
  const gasService = gasServices?.gasMeter ? "gasMeter" : "both";
  const selectDatesElec = (date: Date) => {
    const dateStr: string = formatDateToString(date);
    return !elecDates?.dates.includes(dateStr);
  };

  const selectDatesGas = (date: Date) => {
    const dateStr: string = formatDateToString(date);
    return !gasDates?.dates.includes(dateStr);
  };

  const CloseTransferInfo: React.FC<INFO> = ({ section }) => {
    const firstName =
      HandoverTypeEnum.CLOSE === "CLOSE"
        ? values.accountPrimaryFirstName
        : closeTransfer.accountHolder?.firstName;
    const lastName =
      HandoverTypeEnum.CLOSE === "CLOSE"
        ? values.accountPrimaryLastName
        : closeTransfer.accountHolder?.lastName;
    if (section === ServiceTypeEnum.ELECTRICITY)
      return (
        <>
          {/* customer individual */}
          {!isElectricityEnable &&
            values.electricityAccountPayer === AccountPayer.SOMEONE_ELSE &&
            values.accountHolder === "person" && (
              <Alert tone="info">
                This account is already in the name of {firstName} {lastName}.
              </Alert>
            )}
          {/* customer company */}
          {!isElectricityEnable &&
            values.electricityAccountPayer === AccountPayer.SOMEONE_ELSE &&
            values.accountHolder === "company" && (
              <Alert tone="info">
                This account is already in the name of {values.companyName}.
              </Alert>
            )}
          {/* company */}
          {!isElectricityEnable &&
            values.electricityAccountPayer === AccountPayer.COMPANY && (
              <Alert
                title={`Account not ready to be ${serviceTypeLabelPassive}`}
              >
                Connection is still in progress so the account can't be{" "}
                {serviceTypeLabelPassive}
              </Alert>
            )}
        </>
      );
    return (
      <>
        {!isGasEnable &&
          values[gasService].accountPayer === AccountPayer.SOMEONE_ELSE &&
          values.accountHolder === "person" && (
            <Alert tone="info">
              This account is already in the name of {firstName} {lastName}.
            </Alert>
          )}
        {!isGasEnable &&
          values[gasService].accountPayer === AccountPayer.SOMEONE_ELSE &&
          values.accountHolder === "company" && (
            <Alert tone="info">
              This account is already in the name of {values.companyName}.
            </Alert>
          )}
        {!isGasEnable &&
          values[gasService].accountPayer === AccountPayer.COMPANY && (
            <Alert title={`Account not ready to be ${serviceTypeLabelPassive}`}>
              Connection is still in progress so the account can't be{" "}
              {serviceTypeLabelPassive}
            </Alert>
          )}
      </>
    );
  };

  const onChangeServiceType = (
    event: React.ChangeEvent<HTMLInputElement>,
    serviceType: ServiceTypeEnum
  ) => {
    if (event.target.checked) {
      setFieldValue("closeTransfer.serviceTypes", [
        ...values.closeTransfer.serviceTypes,
        serviceType,
      ]);
    } else {
      setFieldValue(
        "closeTransfer.serviceTypes",
        values.closeTransfer.serviceTypes.filter(
          (service) => service !== serviceType
        )
      );
    }
  };

  const serviceTypeLabel = handoverTypeToLabelMap[closeTransfer.handoverType];
  const serviceTypeLabelPassive =
    handoverTypeToLabelMapPassive[closeTransfer.handoverType];

  const isOnlyOneServiceType = hasElectricitySection !== hasGasSection;

  return (
    <Stack
      space={{
        xs: "xlarge",
        md: "xxlarge",
      }}
    >
      {/* Service */}
      {hasElectricitySection && (
        <Stack
          space={{
            xs: "large",
            md: "xlarge",
          }}
        >
          {isOnlyOneServiceType &&
            !isElectricityReadOnly &&
            (closeTransfer.handoverType ===
            HandoverTypeEnum.CLOSE_AND_TRANSFER ? (
              <Heading variant="h4">Transfer date</Heading>
            ) : (
              <Heading variant="h4">Close date</Heading>
            ))}
          {/* Close */}
          {/* if there is only one service type, the checkbox will be checked by default */}
          {isElectricityReadOnly ? (
            <CTReadOnlyServiceSection
              handoverType={closeTransfer.handoverType}
              isOnlyOneServiceType={isOnlyOneServiceType}
              serviceType={ServiceTypeEnum.ELECTRICITY}
              preferDate={closeTransfer.elecPreferDate}
              onAction={() => onUpdate(ServiceTypeEnum.ELECTRICITY, "action")}
              onCancel={() => onUpdate(ServiceTypeEnum.ELECTRICITY, "cancel")}
            />
          ) : (
            <Stack>
              {!isOnlyOneServiceType && (
                <FormFieldLayout>
                  <Stack space="large">
                    <Heading variant="h4">Electricity</Heading>
                    <CloseTransferInfo section={ServiceTypeEnum.ELECTRICITY} />
                    {isElectricityEnable && (
                      <CheckboxGroup
                        id="close-transfer-electricity-checkbox-group"
                        error
                        helperText={
                          isElectricityEnable &&
                          errors.closeTransfer?.serviceTypes
                            ? "Oops, you must select at least one service to close or transfer."
                            : undefined
                        }
                      >
                        <Checkbox
                          label={`I want to ${serviceTypeLabel} this account`}
                          id="close-transfer-electricity"
                          checked={closeTransfer.serviceTypes.includes(
                            ServiceTypeEnum.ELECTRICITY
                          )}
                          onChange={(event) =>
                            onChangeServiceType(
                              event,
                              ServiceTypeEnum.ELECTRICITY
                            )
                          }
                          disabled={!isElectricityEnable}
                          error={Boolean(errors.closeTransfer?.serviceTypes)}
                        />
                      </CheckboxGroup>
                    )}
                  </Stack>
                </FormFieldLayout>
              )}
              {closeTransfer.serviceTypes.includes(
                ServiceTypeEnum.ELECTRICITY
              ) &&
                isElectricityEnable && (
                  <FormFieldLayout>
                    <Columns collapseBelow="lg">
                      <Column>
                        <DatePickerField
                          name="closeTransfer.elecPreferDate"
                          label="Preferred date (on or after)"
                          shouldDisableDate={selectDatesElec}
                          disabled={!isElectricityEnable}
                        />
                      </Column>
                      <Column />
                    </Columns>
                  </FormFieldLayout>
                )}
            </Stack>
          )}

          {/* End Close */}

          {/* Preferred date */}

          {/* End Preferred date */}
        </Stack>
      )}
      {/* End Service */}

      {/* Service */}
      {hasGasSection && (
        <Stack
          space={{
            xs: "large",
            md: "xlarge",
          }}
        >
          {isOnlyOneServiceType &&
            !isGasReadOnly &&
            (closeTransfer.handoverType ===
            HandoverTypeEnum.CLOSE_AND_TRANSFER ? (
              <Heading variant="h4">Transfer date</Heading>
            ) : (
              <Heading variant="h4">Close date</Heading>
            ))}
          {isGasReadOnly ? (
            <CTReadOnlyServiceSection
              handoverType={closeTransfer.handoverType}
              isOnlyOneServiceType={isOnlyOneServiceType}
              serviceType={ServiceTypeEnum.GAS}
              preferDate={closeTransfer.gasPreferDate}
              onAction={() => onUpdate(ServiceTypeEnum.GAS, "action")}
              onCancel={() => onUpdate(ServiceTypeEnum.GAS, "cancel")}
            />
          ) : (
            <Stack>
              {!isOnlyOneServiceType && (
                <FormFieldLayout>
                  <Stack space="large">
                    <Heading variant="h4">Gas</Heading>
                    <CloseTransferInfo section={ServiceTypeEnum.GAS} />
                    {isGasEnable && (
                      <CheckboxGroup
                        id="close-transfer-gas-checkbox-group"
                        error
                        helperText={
                          isGasEnable && errors.closeTransfer?.serviceTypes
                            ? "Oops, you must select at least one service to close or transfer."
                            : undefined
                        }
                      >
                        <Checkbox
                          label={`I want to ${serviceTypeLabel} this account`}
                          id="close-transfer-gas"
                          checked={values.closeTransfer.serviceTypes.includes(
                            ServiceTypeEnum.GAS
                          )}
                          onChange={(event) =>
                            onChangeServiceType(event, ServiceTypeEnum.GAS)
                          }
                          disabled={!isGasEnable}
                          error={Boolean(errors.closeTransfer?.serviceTypes)}
                        />
                      </CheckboxGroup>
                    )}
                  </Stack>
                </FormFieldLayout>
              )}
              {/* End Close */}
              {/* Preferred date */}
              {values.closeTransfer.serviceTypes.includes(
                ServiceTypeEnum.GAS
              ) &&
                isGasEnable && (
                  <FormFieldLayout>
                    <Columns collapseBelow="lg">
                      <Column>
                        <DatePickerField
                          name="closeTransfer.gasPreferDate"
                          label="Preferred date (on or after)"
                          shouldDisableDate={selectDatesGas}
                          disabled={!isGasEnable}
                        />
                      </Column>
                      <Column />
                    </Columns>
                  </FormFieldLayout>
                )}
            </Stack>
          )}
        </Stack>
      )}
      {/* End Service */}
    </Stack>
  );
};
